import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { kycApi } from '../../api/kyc.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { ApprovalBlockers, ApprovalWarnings } from '../kyc/ApprovalBlockers.js';
import {
  eddPermissionNote,
  mayOfferKeepEddRestriction,
  mayOfferKycApproval,
  MIN_REASON_LENGTH,
} from '../kyc/kyc-approval.js';

/**
 * Approving a customer from their KYC tab (DEV-112).
 *
 * A customer the nightly re-screen moved to "EDD required" — on the same
 * provider match an officer had already reviewed, with no EDD case opened —
 * could not be cleared from anywhere in the back office. This is the same
 * KYC approve route the KYC Queue used (removed, DEV-160): the same
 * readiness gate, a required reason, refused while an EDD case is open, and
 * recorded with the officer's name. Keeping the restriction is the other
 * recorded outcome: "Keep restriction" writes the reason down and leaves the
 * status as it is.
 *
 * Offered to compliance and admin only, and only while the customer is in EDD
 * required or under review. For EDD required the officer also needs the
 * EDD-approval permission (can_approve_edd); without it the button is shown
 * disabled with the reason. The server enforces it from the staff record, and
 * refuses the officer who approved the customer (four eyes) with a plain
 * message that the dialog shows.
 */
export function ApproveKycButton({ customerId, status }: {
  customerId: string;
  status: string;
}): JSX.Element | null {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const offered = mayOfferKycApproval(user, status);
  const permissionNote = eddPermissionNote(user, status);

  // The conditions the approve route enforces, read when the dialog opens.
  const { data: readiness, error: readinessError } = useQuery({
    queryKey: ['kyc-case-readiness', customerId],
    queryFn: async () => kycApi.caseReadiness(customerId),
    enabled: offered && open,
    retry: false,
  });

  const refreshAfterDecision = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['customer', customerId] }),
      queryClient.invalidateQueries({ queryKey: ['customers'] }),
      queryClient.invalidateQueries({ queryKey: ['case-file', customerId] }),
      queryClient.invalidateQueries({ queryKey: ['customer-actions', customerId] }),
      queryClient.invalidateQueries({ queryKey: ['onboarding-applications'] }),
      queryClient.invalidateQueries({ queryKey: ['kyc-case'] }),
      queryClient.invalidateQueries({ queryKey: ['kyc-case-readiness', customerId] }),
    ]);
    setOpen(false);
    setReason('');
  };
  // A refusal may be a new blocker (an EDD case opened meanwhile); re-read
  // the checklist so the dialog shows why.
  const rereadChecklist = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['kyc-case-readiness', customerId] });
  };

  const mutation = useMutation({
    mutationFn: async () => kycApi.approveCase(customerId, reason.trim()),
    onSuccess: refreshAfterDecision,
    onError: rereadChecklist,
  });
  // The other outcome for EDD required: recorded, status unchanged.
  const keepMutation = useMutation({
    mutationFn: async () => kycApi.keepEddRestriction(customerId, reason.trim()),
    onSuccess: refreshAfterDecision,
    onError: rereadChecklist,
  });

  if (!offered && permissionNote !== null) {
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        <Button size="small" variant="outlined" color="success" disabled>Approve</Button>
        <Typography variant="caption" color="text.secondary" data-testid="edd-permission-note">
          {permissionNote}
        </Typography>
      </Stack>
    );
  }
  if (!offered) return null;

  const edd = mayOfferKeepEddRestriction(user, status);
  const pending = mutation.isPending || keepMutation.isPending;
  const reasonUsable = reason.trim().length >= MIN_REASON_LENGTH && reason.length <= 1024;
  const failed = mutation.error ?? keepMutation.error;
  return (
    <>
      <Button size="small" variant="outlined" color="success" onClick={() => {
        setReason('');
        mutation.reset();
        keepMutation.reset();
        setOpen(true);
      }}>
        Approve
      </Button>
      <Dialog open={open} onClose={() => { if (!pending) setOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>{edd ? 'Decide EDD required' : 'Approve KYC'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {edd ? (
              <Alert severity="info">
                This customer is held in EDD required. Approving clears that restriction and
                makes them active; keeping it leaves them restricted. Either way your reason is
                recorded. Both are refused while an EDD case is open: that is decided in the
                EDD review.
              </Alert>
            ) : null}
            <ApprovalBlockers readiness={readiness} />
            {/* Screening warns, it does not block (DEV-114). */}
            <ApprovalWarnings readiness={readiness} />
            {readinessError !== null ? (
              <Alert severity="warning">
                The approval checklist could not be read, so it cannot be confirmed here. The
                server checks it again when you confirm.
              </Alert>
            ) : null}
            <TextField
              autoFocus
              label="Reason (required)"
              multiline
              minRows={3}
              value={reason}
              disabled={pending}
              onChange={(e) => setReason(e.target.value)}
              error={reason.length > 1024}
              helperText={`${String(reason.length)} / 1,024 characters. Recorded with your name on the customer's history and case file.`}
            />
            {failed !== null ? (
              <Alert severity="error">
                The decision was not recorded: {failed.message}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={pending} onClick={() => setOpen(false)}>Cancel</Button>
          {edd ? (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => keepMutation.mutate()}
              disabled={pending || !reasonUsable}
            >
              {keepMutation.isPending ? 'Recording…' : 'Keep restriction'}
            </Button>
          ) : null}
          <Button
            variant="contained"
            color="success"
            onClick={() => mutation.mutate()}
            disabled={pending || readiness?.ready === false || !reasonUsable}
          >
            {mutation.isPending ? 'Recording…' : 'Confirm approve'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
